import { useCallback, useRef } from 'react';
import { message } from 'antd';
import imageCompression from 'browser-image-compression';

const MAX_FILE_SIZE_BYTES = 1024 * 25; // 25ko

export const useProfileImage = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const createFileInput = useCallback(() => {
    if (fileInputRef.current) {
      document.body.removeChild(fileInputRef.current);
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png, image/jpeg, image/jpg';
    input.style.display = 'none';
    document.body.appendChild(input);
    fileInputRef.current = input;

    return input;
  }, []);

  const pickImage = useCallback(
    (onImageSelected: (imageUri: string) => void) => {
      const input = createFileInput();

      input.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];

        if (!file) {
          return;
        }

        const options = {
          maxSizeMB: MAX_FILE_SIZE_BYTES / 1024 / 1024, // converted into MB
          maxWidthOrHeight: 320,
          useWebWorker: true,
        }
        try {
          const compressedFile = await imageCompression(file, options);

          if (compressedFile.size > MAX_FILE_SIZE_BYTES) {
            message.error(`L'image est trop volumineuse, veuillez choisir une image plus petite`);
            return;
          }

          if (!compressedFile.type.startsWith('image/')) {
            message.error('Veuillez sélectionner une image valide (PNG ou JPG)');
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            if (result) {
              onImageSelected(result);
            }
          };
          reader.onerror = () => {
            message.error("Erreur lors de la lecture de l'image");
          };
          reader.readAsDataURL(compressedFile);

          if (fileInputRef.current) {
            document.body.removeChild(fileInputRef.current);
            fileInputRef.current = null;
          }
        } catch (error) {
          console.log(error);
          message.error("Erreur lors de la compression de l'image");
        }
      };

      input.click();
    },
    [createFileInput]
  );

  return { pickImage };
};
